import { PlusIcon } from '@heroicons/react/20/solid';

import { PrimaryButton } from '@components/Buttons/PrimaryButtons';
import { FormikSelect } from '@components/Form';
import {
  CustomInput,
  CustomInputMd,
  CustomInputSm,
} from '@components/Form/CustomInput';
import { CustomSelect } from '@components/Form/CustomSelect';
import { FormikInput } from '@components/Form/FormikInput';
import { TrashIcon } from '@components/Icons';
import { Space } from '@components/Space';
import {
  Table,
  TableContainer,
  TableDataCenter,
  TableDataLeft,
  TableDataRight,
  TableHeaderCenter,
  TableHeaderLeft,
  TableHeaderRight,
} from '@components/TableContainers';
import { discountTypeSelectOptions } from '@models/common/DiscountTypeSelectOptions';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { calculateDiscountAmount } from '@utils/discountUtils';
import { formatAsCurrency } from '@utils/numberFormat';

// Generic interface for line item with sales taxes
export interface BaseLineItem {
  productID?: number;
  description?: string;
  quantity: number;
  price: number;
  subTotal: number;
}

// Generic interface for sales tax
export interface BaseSalesTax {
  salesTaxID: number;
  salesTaxAbbreviation?: string;
  salesTaxAmount: number;
  rate: number;
}

// Props for the LineItemsTable component
export interface LineItemsTableProps<
  TLineItem extends BaseLineItem,
  TSalesTax extends BaseSalesTax
> {
  // Data
  lineItems: TLineItem[];
  products: FormikSelectOption[];
  salesTaxes: FormikSelectOption[];

  // Line item property accessor - key name for sales taxes array
  salesTaxesKey: keyof TLineItem;

  // Handlers
  onProductSelect: (productId: string, index: number) => void;
  onQuantityChange: (index: number, quantity: number) => void;
  onSalesTaxChange: (
    lineItemIndex: number,
    salesTaxIndex: number,
    salesTaxesLength: number,
    salesTaxId: number
  ) => void;
  onAddLineItem: () => void;
  onRemoveLineItem: (index: number) => void;
  onCreateProductClick: () => void;

  // Summary calculation
  getSalesTaxesBySalesTaxID: () => Record<string, TSalesTax[]>;

  // Formik values for discount
  formikValues: {
    isTaxExempt?: boolean;
    discountType?: number;
    discount?: number;
  };

  // Optional: Show discount section (default: true)
  showDiscount?: boolean;
}

export const LineItemsTable = <
  TLineItem extends BaseLineItem,
  TSalesTax extends BaseSalesTax
>({
  lineItems,
  products,
  salesTaxes,
  salesTaxesKey,
  onProductSelect,
  onQuantityChange,
  onSalesTaxChange,
  onAddLineItem,
  onRemoveLineItem,
  onCreateProductClick,
  getSalesTaxesBySalesTaxID,
  formikValues,
  showDiscount = true,
}: LineItemsTableProps<TLineItem, TSalesTax>) => {
  const isTaxExempt = formikValues.isTaxExempt ?? false;

  // Calculate totals
  const subtotal = lineItems.reduce((acc, cur) => acc + cur.subTotal, 0);

  const totalTax = lineItems
    .map((item) => {
      const taxes = item[salesTaxesKey] as unknown as TSalesTax[];
      return taxes?.reduce((acc, cur) => acc + cur.salesTaxAmount, 0) ?? 0;
    })
    .reduce((acc, cur) => acc + cur, 0);

  const discountAmount = calculateDiscountAmount(
    subtotal + (isTaxExempt ? 0 : totalTax),
    Number(formikValues.discountType) || 1,
    formikValues.discount || 0
  );

  const total = subtotal + (isTaxExempt ? 0 : totalTax) - discountAmount;

  return (
    <div className="max-w-5x1 mx-auto">
      <div className="w-full">
        <p className="bg-primary-100 md:text-sm text-xs font-bold text-black py-2 text-center uppercase tracking-wider">
          Details
        </p>
      </div>

      <TableContainer>
        <div className="bg-gray-50">
          <Table>
            <thead className="py-2">
              <tr>
                <TableHeaderLeft>
                  <div className="pl-2">Item Name</div>
                </TableHeaderLeft>
                <TableHeaderLeft>
                  <div className="pl-2">Description</div>
                </TableHeaderLeft>
                <TableHeaderCenter>
                  <div className="pl-2">Quantity</div>
                </TableHeaderCenter>
                <TableHeaderCenter>
                  <div className="pl-2">Price</div>
                </TableHeaderCenter>
                <TableHeaderCenter>
                  <div className="pl-2">Tax</div>
                </TableHeaderCenter>
                <TableHeaderRight>
                  <div className="pl-2">SubTotal</div>
                </TableHeaderRight>
                <TableHeaderRight>
                  <div className="pr-2"></div>
                </TableHeaderRight>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((lineItem, lineItemIndex) => {
                const itemSalesTaxes = lineItem[
                  salesTaxesKey
                ] as unknown as TSalesTax[];

                return (
                  <tr
                    key={lineItemIndex}
                    className={`${
                      lineItemIndex % 2 === 0 ? '' : 'bg-[#56c39225]'
                    }`}
                  >
                    <TableDataLeft>
                      <div className="pl-2 py-2 flex items-stretch md:min-w-[135px] min-w-max">
                        <div className="flex-grow focus-within:z-10">
                          <CustomSelect
                            label=""
                            value={lineItem.productID}
                            selection={products}
                            onChange={(
                              event: React.ChangeEvent<HTMLSelectElement>
                            ) => {
                              onProductSelect(
                                event.target.value,
                                lineItemIndex
                              );
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          className="mt-1 border-l inline-flex items-center px-2 py-2 text-gray-400 bg-white hover:bg-gray-50 text-black"
                          onClick={onCreateProductClick}
                        >
                          <PlusIcon width={15} height={15} />
                        </button>
                      </div>
                    </TableDataLeft>
                    <TableDataLeft>
                      <div className="px-1 py-2 md:min-w-[135px] min-w-max">
                        <CustomInput
                          readonly={true}
                          label=""
                          value={lineItem.description}
                        />
                      </div>
                    </TableDataLeft>
                    <TableDataCenter>
                      <div className="px-1 py-2 mx-auto">
                        <CustomInputSm
                          label=""
                          value={lineItem.quantity}
                          dataType="number"
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            onQuantityChange(
                              lineItemIndex,
                              Number(e.target.value)
                            );
                          }}
                        />
                      </div>
                    </TableDataCenter>
                    <TableDataCenter>
                      <div className="px-1 py-2 mx-auto">
                        <CustomInputSm
                          label=""
                          value={Number(lineItem.price).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                          dataType="number"
                          readonly={true}
                        />
                      </div>
                    </TableDataCenter>

                    <TableDataCenter>
                      <div className="px-1 py-2 md:min-w-[100px] min-w-max">
                        {isTaxExempt ? (
                          <div className="text-xs text-gray-500 py-2 italic">
                            Tax Exempt
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            {itemSalesTaxes?.map((salesTax, salesTaxIndex) => (
                              <CustomSelect
                                key={salesTaxIndex}
                                label=""
                                value={salesTax.salesTaxID}
                                selection={salesTaxes || []}
                                onChange={(
                                  e: React.ChangeEvent<HTMLSelectElement>
                                ) => {
                                  onSalesTaxChange(
                                    lineItemIndex,
                                    salesTaxIndex,
                                    itemSalesTaxes.length,
                                    Number(e.target.value)
                                  );
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </TableDataCenter>
                    <TableDataRight>
                      <div className="px-1 py-2 ml-auto">
                        <CustomInputMd
                          label=""
                          value={Number(lineItem.subTotal).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                          dataType="number"
                          readonly={true}
                        />
                      </div>
                    </TableDataRight>
                    <TableDataRight>
                      <div className="pr-4 py-2 flex justify-end">
                        <button
                          className="py-2 text-gray-400 hover:text-red-500 transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            onRemoveLineItem(lineItemIndex);
                          }}
                        >
                          <TrashIcon height="20" width="20" />
                        </button>
                      </div>
                    </TableDataRight>
                  </tr>
                );
              })}

              <tr>
                <TableDataLeft>
                  <PrimaryButton onClick={onAddLineItem}>
                    Add Item
                  </PrimaryButton>
                </TableDataLeft>
              </tr>

              <tr>
                <td colSpan={7}>
                  <div className="p-4">
                    {/* Subtotal */}
                    <div className="flex lg:text-sm md:text-xs text-xs justify-end">
                      <div className="font-bold mr-4">Subtotal:</div>
                      <div className="w-24 text-right">
                        {formatAsCurrency(subtotal)}
                      </div>
                    </div>

                    <Space />

                    {/* Discount Input */}
                    {showDiscount && (
                      <>
                        <div className="flex lg:text-sm md:text-xs text-xs justify-end">
                          <div className="w-15 mr-4 mt-5">
                            <FormikSelect
                              label=" "
                              name="discountType"
                              selection={discountTypeSelectOptions}
                            />
                          </div>
                          <div className="w-44">
                            <FormikInput
                              label="Discount"
                              name="discount"
                              dataType="number"
                            />
                          </div>
                        </div>

                        <Space />
                      </>
                    )}

                    {/* Tax breakdown */}
                    {!isTaxExempt && (
                      <>
                        {Object.entries(getSalesTaxesBySalesTaxID()).map(
                          ([abbreviation, details]) => (
                            <div
                              className="flex lg:text-sm md:text-xs text-xs justify-end"
                              key={abbreviation}
                            >
                              <div className="mr-4">
                                {abbreviation} ({details[0]?.rate}%)
                              </div>
                              <div className="w-24 text-right">
                                {formatAsCurrency(
                                  details.reduce(
                                    (acc, cur) => acc + cur.salesTaxAmount,
                                    0
                                  )
                                )}
                              </div>
                            </div>
                          )
                        )}

                        <div className="flex lg:text-sm md:text-xs text-xs justify-end">
                          <div className="font-bold mr-4">Total Tax:</div>
                          <div className="w-24 text-right">
                            {formatAsCurrency(totalTax)}
                          </div>
                        </div>

                        <Space />
                      </>
                    )}

                    {/* Tax exempt message */}
                    {isTaxExempt && (
                      <>
                        <div className="flex lg:text-sm md:text-xs text-xs justify-end">
                          <div className="mr-4 italic text-gray-600">
                            Tax Exempt - No taxes applied
                          </div>
                          <div className="w-24 text-right">
                            {formatAsCurrency(0)}
                          </div>
                        </div>

                        <Space />
                      </>
                    )}

                    {/* Discount display */}
                    {showDiscount && (
                      <>
                        <div className="flex lg:text-sm md:text-xs text-xs justify-end">
                          <div className="font-bold mr-4">
                            Discount{' '}
                            {Number(formikValues.discountType) === 2
                              ? `(${formikValues.discount}%)`
                              : ''}
                            :
                          </div>
                          <div className="w-24 text-right">
                            {formatAsCurrency(discountAmount)}
                          </div>
                        </div>

                        <Space />
                      </>
                    )}

                    {/* Total */}
                    <div className="flex lg:text-sm md:text-xs text-xs justify-end">
                      <div className="font-bold mr-4">Total:</div>
                      <div className="w-24 text-right">
                        {formatAsCurrency(total)}
                      </div>
                    </div>
                  </div>
                </td>
                <td></td>
              </tr>
            </tbody>
          </Table>
        </div>
      </TableContainer>
    </div>
  );
};

export default LineItemsTable;
